import AddIcon from "@mui/icons-material/Add";
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import Field from "components/Field";
import { useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import AddNewOptionInput from "./AddNewOptionInput";

const otherValue = "__add__";

export type CreatableSelectFieldProps = {
  name: string;
  label: string;
  options: string[];
  required?: boolean;
  disabled?: boolean;
  maxLength?: number;
};

const findOptionIgnoringCase = (options: string[], value: string) =>
  options.find(
    (option) => option.trim().toLowerCase() === value.trim().toLowerCase()
  );

/**
 * Single-value select over a list of existing options, ending with an "Other" item that lets the user type a new
 * one. A typed value matching an existing option ignoring case is replaced by that option, so the list does not
 * fork on casing. While a new option is being typed the field value is cleared, so the form cannot be submitted
 * with the previous selection; cancelling restores it.
 */
const CreatableSelectField = ({
  name,
  label,
  options,
  required = false,
  disabled = false,
  maxLength,
}: Readonly<CreatableSelectFieldProps>) => {
  const { formatMessage: t } = useIntl();
  const labelId = `${name}-label`;
  const [adding, setAdding] = useState(false);
  const valueBeforeAdding = useRef<string>("");
  const selectRef = useRef<HTMLInputElement>(null);
  const isFirstRender = useRef(true);

  const distinctOptions = useMemo(() => Array.from(new Set(options)), [
    options,
  ]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (!adding) {
      selectRef.current?.focus();
    }
  }, [adding]);

  return (
    <Field name={name} required={required}>
      {({ input, meta }) => {
        const value: string = input.value ?? "";
        const isCustomValue = value !== "" && !distinctOptions.includes(value);
        const showError = !!meta.error && meta.submitFailed;

        const handleChange = (event: SelectChangeEvent<string>) => {
          const selected = event.target.value;

          if (selected === otherValue) {
            return;
          }

          input.onChange(selected);
        };

        const startAdding = () => {
          valueBeforeAdding.current = value;
          input.onChange("");
          setAdding(true);
        };

        const cancelAdding = () => {
          input.onChange(valueBeforeAdding.current);
          setAdding(false);
        };

        const handleAdd = (newOption: string) => {
          input.onChange(
            findOptionIgnoringCase(distinctOptions, newOption) ?? newOption
          );
          setAdding(false);
        };

        return (
          <FormControl
            fullWidth
            variant="standard"
            required={required}
            disabled={disabled}
            error={showError}
          >
            {adding ? (
              <AddNewOptionInput
                label={label}
                required={required}
                maxLength={maxLength}
                error={showError}
                helperText={
                  showError
                    ? t({ id: "creatableSelectNewOptionHelp" })
                    : undefined
                }
                onAdd={handleAdd}
                onCancel={cancelAdding}
              />
            ) : (
              <>
                <InputLabel id={labelId}>{label}</InputLabel>
                <Select
                  labelId={labelId}
                  name={input.name}
                  value={value}
                  onChange={handleChange}
                  onBlur={input.onBlur}
                  onFocus={input.onFocus}
                  inputRef={selectRef}
                >
                  {distinctOptions.map((option) => (
                    <MenuItem dense key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                  {isCustomValue && (
                    <MenuItem dense key={value} value={value}>
                      {value}
                    </MenuItem>
                  )}
                  <MenuItem dense value={otherValue} onClick={startAdding}>
                    <AddIcon fontSize="small" sx={{ mr: 0.5 }} />
                    {t({ id: "creatableSelectOther" })}
                  </MenuItem>
                </Select>
              </>
            )}
            {showError && !adding && (
              <FormHelperText>{meta.error}</FormHelperText>
            )}
          </FormControl>
        );
      }}
    </Field>
  );
};

export default CreatableSelectField;
